import React, { useEffect, useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Download, MessageSquareText, Receipt, Printer, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { openReceiptPrint } from "../lib/receiptPrint";

const TX_LABEL = {
  GOODS_RECEIPT: "Wareneingang",
  GOODS_ISSUE: "Warenausgang",
  TRANSFER: "Umlagerung",
  INVENTORY_CORRECTION: "Korrektur",
  VENDING_MACHINE_SALE: "Automaten-Verkauf",
  POS_SALE: "Kassenverkauf",
  RETURN: "Retoure",
};
const TX_COLOR = {
  GOODS_RECEIPT: "text-emerald-400",
  GOODS_ISSUE: "text-rose-400",
  TRANSFER: "text-violet-400",
  INVENTORY_CORRECTION: "text-yellow-400",
};
// Operations Polish 2026-08-30: WA-Subtyp / Grund als Filter + CSV.
const WA_REASON_LABEL = {
  eigengebrauch: "Eigengebrauch",
  defekt: "Defekt",
  verlust: "Verlust",
  retoure: "Retoure / Rückgabe",
  sonstiges: "Sonstiges",
};

// RFC-4180 safe CSV cell — surround with double-quotes and escape embedded quotes.
function csvCell(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return `"${s.replace(/"/g, '""')}"`;
}

export default function Buchungshistorie() {
  const [params, setParams] = useSearchParams();
  const [type, setType] = useState(() => {
    const t = params.get("type");
    return t && (t in TX_LABEL) ? t : "all";
  });
  const [locationId, setLocationId] = useState("all");
  // Operations Polish 2026-08-30: Filter „Grund" (Subtyp). Aktiv nur
  // wenn Typ = alle oder GOODS_ISSUE; wird sonst UI-seitig ausgegraut.
  const [subtype, setSubtype] = useState("all");
  const [noteTx, setNoteTx] = useState(null);
  // V1b — Beleg-Aktion pro operation_id (erzeugen / anzeigen / regenerieren / löschen).
  const [receiptOpId, setReceiptOpId] = useState(null);

  // Sync type ↔ URL — Dashboard-Kacheln „WE heute" / „WA heute" springen
  // hierher mit ?type=GOODS_RECEIPT / ?type=GOODS_ISSUE.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (type === "all") next.delete("type"); else next.set("type", type);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const locations = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations")).data,
  });
  const txs = useQuery({
    queryKey: ["txs", type, locationId, subtype],
    queryFn: async () => {
      const rows = (await api.get("/transactions", {
        params: {
          type: type === "all" ? undefined : type,
          location_id: locationId === "all" ? undefined : locationId,
        },
      })).data;
      // Client-Filter nach Subtyp — backend liefert `subtype` mit,
      // Filter bewusst clientseitig, damit kombinierbar mit allen
      // bestehenden Filtern bleibt und der `/transactions`-Endpoint
      // additiv erweiterbar ist.
      if (subtype === "all") return rows;
      return rows.filter((t) => t.subtype === subtype);
    },
  });

  // V1b — welche operation_ids der aktuellen Sicht haben bereits einen Beleg?
  // Ein einziger GET, gefiltert clientseitig. Ausreichend performant für die
  // typische Ledger-Sicht (limit 500).
  const receiptsIdx = useQuery({
    queryKey: ["booking-receipts-index"],
    queryFn: async () => (await api.get("/booking-receipts")).data,
    staleTime: 30_000,
  });
  const receiptByOp = useMemo(() => {
    const m = {};
    (receiptsIdx.data || []).forEach((r) => { m[r.operation_id] = r; });
    return m;
  }, [receiptsIdx.data]);

  // Block-Gruppierung nach operation_id: aufeinanderfolgende Ledger-Zeilen
  // mit derselben operation_id gehören zum selben Buchungsvorgang und
  // teilen sich Zebra-Farbe + eine einzige Beleg-Aktion. Zeilen ohne
  // operation_id (Alt-Buchungen, POS/Vending-Sales) sind Einzelblöcke.
  const blocks = useMemo(() => {
    const rows = txs.data || [];
    const out = [];
    let cur = null;
    rows.forEach((r) => {
      const key = r.operation_id || `single:${r.id}`;
      if (!cur || cur.key !== key) {
        cur = { key, opId: r.operation_id || null, rows: [] };
        out.push(cur);
      }
      cur.rows.push(r);
    });
    return out;
  }, [txs.data]);

  const exportCsv = () => {
    const rows = txs.data || [];
    const header = [
      "Zeitpunkt", "Typ", "Grund", "Produkt", "EAN", "Menge",
      "Von", "Nach", "Vorher", "Nachher", "Benutzer", "Bemerkung",
    ];
    const csv = [
      header.map(csvCell).join(","),
      ...rows.map((t) => [
        t.created_at,
        TX_LABEL[t.type] || t.type,
        t.subtype ? (WA_REASON_LABEL[t.subtype] || t.subtype) : "",
        t.product_name || "",
        t.product_ean || "",
        t.quantity,
        t.from_location_name || "",
        t.to_location_name || "",
        t.previous_stock ?? "",
        t.resulting_stock ?? "",
        t.user_email || "",
        t.notes || "",
      ].map(csvCell).join(",")),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `buchungshistorie_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="ledger-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-cyan-400">Audit</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight truncate">Buchungshistorie</h1>
        </div>
        <Button
          data-testid="ledger-export-btn"
          onClick={exportCsv} variant="outline"
          className="border-zinc-700 rounded-sm hover:bg-zinc-800 uppercase tracking-wider w-full sm:w-auto"
        >
          <Download size={14} className="mr-2" /> CSV Export
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="ledger-filter-type" className="h-11 bg-zinc-900 border-zinc-800 rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            <SelectItem value="all">Alle Typen</SelectItem>
            {Object.entries(TX_LABEL).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={locationId} onValueChange={setLocationId}>
          <SelectTrigger data-testid="ledger-filter-loc" className="h-11 bg-zinc-900 border-zinc-800 rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            <SelectItem value="all">Alle Lagerorte</SelectItem>
            {(locations.data || []).map((l) => (
              <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={subtype} onValueChange={setSubtype}>
          <SelectTrigger
            data-testid="ledger-filter-subtype"
            className="h-11 bg-zinc-900 border-zinc-800 rounded-sm"
          >
            <SelectValue placeholder="Grund" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            <SelectItem value="all">Alle Gründe (WA)</SelectItem>
            {Object.entries(WA_REASON_LABEL).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-zinc-800 bg-zinc-900 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
            <tr>
              <th className="text-left px-4 py-3 font-mono">Zeitpunkt</th>
              <th className="text-left px-4 py-3">Typ</th>
              <th className="text-left px-4 py-3">Produkt</th>
              <th className="text-right px-4 py-3 font-mono">Menge</th>
              <th className="text-left px-4 py-3">Quelle → Ziel</th>
              <th className="text-right px-4 py-3 font-mono">Vorher</th>
              <th className="text-right px-4 py-3 font-mono">Nachher</th>
              <th className="text-left px-4 py-3">Benutzer</th>
              <th className="px-3 py-3 text-center">Notiz</th>
              <th className="px-3 py-3 text-center">Beleg</th>
            </tr>
          </thead>
          <tbody data-testid="ledger-table-body">
            {blocks.map((block, blockIdx) => {
              // Blockhinterlegung: dezenter weißer Overlay-Alpha —
              // theme-agnostisch (funktioniert über dunklem und hellem Grund),
              // klar sichtbar in beiden Themes, ohne die Zeilen zu erdrücken.
              // Kein Hover-Effekt mehr — der normale Zustand entspricht dem
              // früheren Hover-Level.
              const bg = blockIdx % 2 === 0 ? "bg-white/[0.06]" : "bg-white/[0.02]";
              const hasReceipt = block.opId ? receiptByOp[block.opId] : null;
              const eligibleForReceipt =
                !!block.opId &&
                ["GOODS_RECEIPT", "GOODS_ISSUE", "TRANSFER"].includes(block.rows[0].type);
              return block.rows.map((t, rowIdx) => {
                const isFirst = rowIdx === 0;
                const isLast = rowIdx === block.rows.length - 1;
                const rowCls = [
                  bg,
                  isFirst && blockIdx > 0 ? "border-t border-zinc-800" : "",
                  isLast ? "border-b border-zinc-800/70" : "",
                ].filter(Boolean).join(" ");
                return (
                  <tr key={t.id} className={rowCls} data-testid={`ledger-row-${t.id}`}>
                    <td className="px-4 py-2 font-mono text-xs text-zinc-400 whitespace-nowrap">
                      {new Date(t.created_at).toLocaleString("de-DE")}
                    </td>
                    <td className={`px-4 py-2 text-xs uppercase tracking-wider ${TX_COLOR[t.type] || ""}`}>
                      {TX_LABEL[t.type] || t.type}
                      {t.subtype && (
                        <div
                          data-testid={`ledger-row-subtype-${t.id}`}
                          className="mt-0.5 text-[10px] normal-case tracking-normal text-zinc-500"
                        >
                          {WA_REASON_LABEL[t.subtype] || t.subtype}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 min-w-0">
                      <div className="truncate">{t.product_name || "—"}</div>
                      {t.product_ean && <div className="text-xs text-zinc-500 font-mono truncate">{t.product_ean}</div>}
                      {t.has_notes && (
                        <div className="hidden md:block text-xs text-zinc-400 mt-0.5 truncate italic">
                          „{t.notes}"
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums font-semibold">{t.quantity}</td>
                    <td className="px-4 py-2 text-xs text-zinc-300">
                      {t.from_location_name || "—"} → {t.to_location_name || "—"}
                    </td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums text-zinc-500">{t.previous_stock ?? "—"}</td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums">{t.resulting_stock ?? "—"}</td>
                    <td className="px-4 py-2 text-xs text-zinc-400 truncate max-w-[180px]">{t.user_email || "—"}</td>
                    <td className="px-3 py-2 text-center">
                      {t.has_notes ? (
                        <button
                          data-testid={`ledger-note-btn-${t.id}`}
                          onClick={() => setNoteTx(t)}
                          className="text-cyan-400 hover:text-cyan-300 inline-flex items-center justify-center"
                          title="Notiz anzeigen"
                          aria-label="Notiz anzeigen"
                        >
                          <MessageSquareText size={16} />
                        </button>
                      ) : (
                        <span className="text-zinc-700">—</span>
                      )}
                    </td>
                    {isFirst && (
                      <td
                        rowSpan={block.rows.length}
                        className="px-3 py-2 text-center whitespace-nowrap align-top border-l border-zinc-800/60"
                      >
                        {eligibleForReceipt ? (
                          <button
                            data-testid={`ledger-receipt-btn-${block.opId}`}
                            onClick={() => setReceiptOpId(block.opId)}
                            className={hasReceipt
                              ? "text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest"
                              : "text-zinc-400 hover:text-cyan-300 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest"}
                            title={hasReceipt ? `Beleg ${hasReceipt.receipt_number}` : "Beleg erzeugen"}
                          >
                            <Receipt size={14} />
                            {hasReceipt ? hasReceipt.receipt_number.replace("LB-", "") : "Beleg"}
                          </button>
                        ) : (
                          <span className="text-zinc-700">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              });
            })}
            {!txs.data?.length && (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-zinc-500">Noch keine Buchungen erfasst.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!noteTx} onOpenChange={(o) => !o && setNoteTx(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800" data-testid="ledger-note-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">Bemerkung</DialogTitle>
          </DialogHeader>
          {noteTx && (
            <div className="space-y-3">
              <div className="text-xs text-zinc-500 font-mono">
                {new Date(noteTx.created_at).toLocaleString("de-DE")} · {TX_LABEL[noteTx.type] || noteTx.type}
              </div>
              <div className="text-sm text-zinc-300">
                {noteTx.product_name || "—"}
                {noteTx.product_ean && <span className="text-zinc-500 font-mono ml-2">{noteTx.product_ean}</span>}
              </div>
              <div
                data-testid="ledger-note-body"
                className="whitespace-pre-wrap break-anywhere text-sm bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-zinc-100"
              >
                {noteTx.notes}
              </div>
              <div className="text-xs text-zinc-500">— {noteTx.user_email || "system"}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ReceiptActionDialog
        operationId={receiptOpId}
        onClose={() => setReceiptOpId(null)}
      />
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* V1b — Beleg-Aktion aus Buchungshistorie                          */
/* ---------------------------------------------------------------- */

function ReceiptActionDialog({ operationId, onClose }) {
  const qc = useQueryClient();
  const enabled = !!operationId;
  const receipt = useQuery({
    queryKey: ["booking-receipt-by-op", operationId],
    queryFn: async () => {
      try {
        return (await api.get(`/booking-receipts/by-operation/${operationId}`)).data;
      } catch (e) {
        if (e.response?.status === 404) return null;
        throw e;
      }
    },
    enabled,
  });

  const createMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/booking-receipts/from-operation/${operationId}`)).data,
    onSuccess: (br) => {
      toast.success(`Beleg ${br.receipt_number} erzeugt`);
      qc.invalidateQueries({ queryKey: ["booking-receipts"] });
      qc.invalidateQueries({ queryKey: ["booking-receipts-index"] });
      qc.invalidateQueries({ queryKey: ["booking-receipt-by-op", operationId] });
    },
    onError: (e) =>
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const deleteMut = useMutation({
    mutationFn: async () => {
      const cur = receipt.data;
      if (!cur) return null;
      await api.delete(`/booking-receipts/${cur.id}`);
    },
    onSuccess: () => {
      toast.success("Beleg gelöscht — Transaktionen und Bestand bleiben.");
      qc.invalidateQueries({ queryKey: ["booking-receipts"] });
      qc.invalidateQueries({ queryKey: ["booking-receipts-index"] });
      qc.invalidateQueries({ queryKey: ["booking-receipt-by-op", operationId] });
    },
    onError: (e) =>
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  if (!enabled) return null;

  const br = receipt.data;

  return (
    <Dialog open={enabled} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="ledger-receipt-dialog"
        className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wide flex items-center gap-2">
            <Receipt size={18} className="text-cyan-400" />
            {br ? br.receipt_number : "Buchungsbeleg"}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs break-all">
            Vorgangs-ID: <span className="font-mono">{operationId}</span>
          </DialogDescription>
        </DialogHeader>

        {receipt.isLoading && (
          <div className="text-zinc-500 text-sm">Lade Beleg-Status…</div>
        )}

        {!receipt.isLoading && !br && (
          <div className="space-y-3">
            <div className="text-sm text-zinc-300">
              Für diese Vorgangs-ID existiert noch kein Buchungsbeleg.
            </div>
            <div className="text-xs text-zinc-500">
              Beleg wird nur aus der operation_id abgeleitet — Ledger und
              Bestand bleiben unangetastet. Bereits gelöschte Belege lassen
              sich hier neu erzeugen (mit neuer, fortlaufender Belegnummer).
            </div>
            <Button
              data-testid="ledger-receipt-create"
              onClick={() => createMut.mutate()}
              disabled={createMut.isPending}
              className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 uppercase tracking-widest font-bold rounded-sm"
            >
              <Plus size={14} className="mr-2" />
              {createMut.isPending ? "Erzeuge…" : "Beleg jetzt erzeugen"}
            </Button>
          </div>
        )}

        {br && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <MetaRow label="Typ" value={TX_LABEL[br.booking_type] || br.booking_type} />
              <MetaRow label="Zeitpunkt" value={new Date(br.occurred_at).toLocaleString("de-DE")} />
              <MetaRow label="Von" value={br.from_location_name || "—"} />
              <MetaRow label="Nach" value={br.to_location_name || "—"} />
              <MetaRow label="Erfasst durch" value={br.user_email || "—"} />
              <MetaRow label="Positionen" value={(br.items || []).length} />
            </div>
            {br.notes && (
              <div className="text-sm text-zinc-300 italic border-l-2 border-zinc-700 pl-3">
                „{br.notes}"
              </div>
            )}
            <div className="border-t border-zinc-800 pt-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                Positionen (Snapshot)
              </div>
              <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
                {(br.items || []).map((it, i) => (
                  <div key={i} className="text-xs flex justify-between gap-2 border border-zinc-800 rounded-sm px-2 py-1.5">
                    <span className="truncate">
                      {it.product_name || it.product_id}
                      {it.product_ean && <span className="ml-2 text-zinc-500 font-mono">{it.product_ean}</span>}
                    </span>
                    <span className="font-mono tabular-nums shrink-0">
                      ×{it.quantity} · {it.previous_stock ?? "—"}→{it.resulting_stock ?? "—"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          <Button
            data-testid="ledger-receipt-close"
            variant="outline"
            className="border-zinc-700 rounded-sm"
            onClick={onClose}
          >
            Schließen
          </Button>
          {br && (
            <>
              <Button
                data-testid="ledger-receipt-delete"
                variant="outline"
                className="border-rose-800 text-rose-400 hover:bg-rose-950/60 rounded-sm"
                onClick={() => {
                  if (window.confirm(
                    `Beleg ${br.receipt_number} löschen?\n\nTransaktionen und Bestand bleiben unangetastet — der Beleg lässt sich aus derselben operation_id jederzeit neu erzeugen.`
                  )) deleteMut.mutate();
                }}
                disabled={deleteMut.isPending}
              >
                <Trash2 size={14} className="mr-2" />
                Löschen
              </Button>
              <Button
                data-testid="ledger-receipt-print"
                className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 uppercase tracking-widest font-bold rounded-sm"
                onClick={() => openReceiptPrint(br)}
              >
                <Printer size={14} className="mr-2" />
                Drucken
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MetaRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-100 break-anywhere">{value}</div>
    </div>
  );
}
